import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { LogOut, HelpCircle } from "lucide-react";

import { formatApiErrorDetail } from "@/lib/api";
import EditorialTemplate from "./templates/EditorialTemplate";
import MosaicTemplate from "./templates/MosaicTemplate";
import CinemaTemplate from "./templates/CinemaTemplate";
import PremiumPasswordGate from "./shared/PremiumPasswordGate";
import PremiumLightbox from "./shared/PremiumLightbox";
import CollaboratorGate from "./shared/CollaboratorGate";
import FavoritesViewToggle from "./shared/FavoritesViewToggle";
import InteractiveHeart from "./shared/InteractiveHeart";
import CommentDrawer from "./shared/CommentDrawer";
import CommentBadge from "./shared/CommentBadge";
import SelectionSubmitModal from "./shared/SelectionSubmitModal";
import SelectionFooterCta from "./shared/SelectionFooterCta";
import InteractiveOnboardingModal from "./shared/InteractiveOnboardingModal";
import StorageStatusBanner from "./shared/StorageStatusBanner";
import LiveSessionPanel from "./shared/LiveSessionPanel";
import PhotoLiveOverlay from "./shared/PhotoLiveOverlay";
import PrimaryNotificationsBell from "./shared/PrimaryNotificationsBell";
import { Radio } from "lucide-react";
import useCollaborator from "./shared/useCollaborator";
import useGalleryFavorites from "./shared/useGalleryFavorites";
import useGalleryComments from "./shared/useGalleryComments";
import useGalleryTheme from "./shared/useGalleryTheme";
import usePageMeta from "@/hooks/usePageMeta";
import { LoadingState, ErrorState, SubmittedView, WelcomeBackBanner } from "./states";
import { PhaseBanner, DownloadBar } from "./DeliveryBar";
import SelectionBar from "./SelectionBar";
import SubmitModal from "./SubmitModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const DESIGN_PREVIEW_PHOTOS = 24;

const TEMPLATES = {
  editorial: EditorialTemplate,
  mosaic: MosaicTemplate,
  cinema: CinemaTemplate,
};

/**
 * Public gallery orchestrator. Owns ALL state — auth, favorites, downloads,
 * lightbox, slideshow — and hands a stable `ui` + `actions` bundle to whichever
 * template the photographer selected (Editorial / Mosaic / Cinema).
 *
 * Interactive galleries (type === 'interactive') get an additional collaborator
 * identity layer (Phase G1) and server-persisted favorites (Phase G2) plumbed
 * through `ui.interactive`. Standard galleries are unchanged.
 */
export default function PublicGallery() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Admin Design Studio embeds this page with ?design=1 — a lightweight mode
  // (capped photo count, ≤1200px variants) so the preview loads fast on a
  // CPU-constrained pod instead of taking minutes.
  const isDesign = new URLSearchParams(location.search).get("design") === "1";
  // How many photos the studio preview renders: 24 | 60 | 0 (= all). Seeded
  // from ?n= and updated live via `lumetier-preview-limit` (no reload).
  const [previewLimit, setPreviewLimit] = useState(() => {
    const n = new URLSearchParams(window.location.search).get("n");
    if (n === "all") return 0;
    const v = parseInt(n, 10);
    return Number.isFinite(v) && v > 0 ? v : DESIGN_PREVIEW_PHOTOS;
  });
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [authenticating, setAuthenticating] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  // Per-gallery meta — title pulls from the gallery name once loaded.
  // Pre-auth (gate visible) we still set a generic title so social shares are
  // never the global "Lumetier · The operating system for photographers" leak.
  usePageMeta({
    title: gallery?.name
      ? `${gallery.name} — Gallery`
      : "Private Gallery — Lumetier",
    description: gallery?.name
      ? `View and select your favorites from the ${gallery.name} photography session.`
      : "A private client gallery hosted on Lumetier.",
    canonical: `https://lumetiercreatives.com/gallery/${slug}`,
    noIndex: true, // client galleries are password-gated, never want them indexed
  });

  // Proofing state (local — used by all galleries for selection submission).
  // Seeded from localStorage so an in-progress pick list survives refreshes.
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(`lumetier_proof_favs_${window.location.pathname.split("/").pop()}`);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  // Previous submitted selection (returning-client narrow-down workflow)
  const [prevSelection, setPrevSelection] = useState(null);
  // "all" | "selected" — lets the client view only their picked photos
  const [proofView, setProofView] = useState("all");
  const [showSubmit, setShowSubmit] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [overallMessage, setOverallMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedResult, setSubmittedResult] = useState(null);

  // Delivery state
  const [downloadPassword, setDownloadPassword] = useState("");
  const [downloadUnlocked, setDownloadUnlocked] = useState(false);
  const [verifyingDl, setVerifyingDl] = useState(false);
  const [zipping, setZipping] = useState(false);

  // Lightbox / slideshow
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [slideshowOn, setSlideshowOn] = useState(false);

  // Interactive (G2): per-collaborator favorites view filter
  const [favView, setFavView] = useState("all");

  // Primary magic-link claim race guard: when the page boots with
  // `?primary_token=…` in the URL, claim-primary fires inside a useEffect
  // (one microtask after mount). On the very first render the predicate
  // `!collab.token && !collab.loading` is already true → without this flag
  // the CollaboratorGate would flash for one paint before the token lands.
  // Initialized synchronously from window.location so it's true on render 0.
  const [claimingPrimary, setClaimingPrimary] = useState(() => {
    try {
      return !!new URLSearchParams(window.location.search).get("primary_token");
    } catch {
      return false;
    }
  });

  const { style: themeStyle, accent } = useGalleryTheme(gallery);

  // Is this an interactive gallery? Determined from the (gated) loaded payload.
  const isInteractive = gallery?.type === "interactive";

  // Collaborator identity (interactive galleries only — hook is mounted always
  // but only consulted when needed; the localStorage read is cheap)
  const collab = useCollaborator(slug);

  // Server-side favorites (G2). Disabled for standard galleries via `enabled`.
  const favsServer = useGalleryFavorites({
    slug,
    token: collab.token,
    enabled: isInteractive && !!collab.token,
  });

  // Per-photo comments (G3). Same enable predicate as favorites.
  const commentsHook = useGalleryComments({
    slug,
    token: collab.token,
    enabled: isInteractive && !!collab.token,
  });
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [commentDrawerPhoto, setCommentDrawerPhoto] = useState(null);
  const openCommentsFor = useCallback((photo) => {
    setCommentDrawerPhoto(photo);
    setCommentDrawerOpen(true);
  }, []);

  // G4 — selection submit modal
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const isLockedSubmitted = gallery?.status === "selections_received" || submitted;

  // Interactive onboarding splash — auto-opens on first visit per
  // collaborator (gated on localStorage), reopenable on demand via the "?"
  // pill in the interactive header. Storage key is scoped to the gallery
  // slug + collaborator id so each family member sees the explainer once
  // on their own first visit.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const onboardingKey = collab.me?.id ? `lumetier_interactive_onboarded_${slug}_${collab.me.id}` : null;
  useEffect(() => {
    if (!isInteractive || !onboardingKey) return;
    try {
      if (!localStorage.getItem(onboardingKey)) setOnboardingOpen(true);
    } catch {
      setOnboardingOpen(true);
    }
  }, [isInteractive, onboardingKey]);
  function closeOnboarding() {
    if (onboardingKey) {
      try { localStorage.setItem(onboardingKey, String(Date.now())); } catch { /* best-effort */ }
    }
    setOnboardingOpen(false);
  }

  // G5 — live session
  const { roomId: routeRoomId } = useParams();    // when the route is /gallery/:slug/live/:roomId
  const [liveRoomId, setLiveRoomId] = useState(routeRoomId || null);
  const [liveShareUrl, setLiveShareUrl] = useState("");
  const [liveOpen, setLiveOpen] = useState(!!routeRoomId);
  // G5.b — live signals exposed to templates so each photo card can render
  // viewer-presence avatars + heart-burst animations.
  const [liveParticipants, setLiveParticipants] = useState([]);
  const [liveMeId, setLiveMeId] = useState(null);
  const [liveBursts, setLiveBursts] = useState({});  // { [photo_id]: incrementing counter }
  // Imperative WS-send hook (set by LiveSessionPanel via callback). We use it
  // to auto-emit a `heart_burst` whenever the user toggles a real favorite.
  const liveSendRef = useRef(null);
  async function startOrResumeLive() {
    if (!collab.token) return;
    try {
      // If already have a route room id, just open the panel
      if (liveRoomId) { setLiveOpen(true); return; }
      const { data } = await axios.post(
        `${API}/galleries/${slug}/live/rooms`,
        {},
        { headers: { Authorization: `Bearer ${collab.token}` } },
      );
      setLiveRoomId(data.room_id);
      setLiveShareUrl(data.share_url);
      setLiveOpen(true);
      toast.success("Live session started — share link copied");
      try { await navigator.clipboard?.writeText(data.share_url); } catch (e) { void e; }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't start live session");
    }
  }

  // ── Data loading ────────────────────────────────────────────────────
  const loadGallery = useCallback(async (pw) => {
    setLoading(true);
    setError("");
    try {
      const headers = pw ? { "x-gallery-password": pw } : {};
      const { data } = await axios.get(`${API}/galleries/${slug}`, { headers });
      setGallery(data);
      // Kick off the hero cover download immediately (high priority) instead
      // of waiting for the template to mount and the CSS background-image to
      // be discovered — shaves noticeable time off the cover's first paint.
      const rawCover = data?.cover_image || data?.photos?.[0]?.url;
      if (rawCover && data?.unlocked && !isDesign) {
        const pre = new Image();
        pre.fetchPriority = "high";
        pre.src = `${BACKEND}${rawCover}${rawCover.includes("?") ? "&" : "?"}w=2000&q=88`;
      }
      if (data?.unlocked) setUnlocked(true);
      if (pw && data?.unlocked) {
        try { sessionStorage.setItem(`gallery_pw_${slug}`, pw); } catch (err) { console.warn(err); }
      }
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 404) setError("Gallery not found. The link may be incorrect or expired.");
      else setError(detail || "Couldn't load gallery");
    } finally {
      setLoading(false);
    }
  }, [slug, isDesign]);

  useEffect(() => {
    const stored = (() => {
      try { return sessionStorage.getItem(`gallery_pw_${slug}`); } catch (err) { console.warn(err); return null; }
    })();
    if (stored) { setPassword(stored); loadGallery(stored); }
    else loadGallery();
  }, [slug, loadGallery]);

  // Live design preview: the admin Design tab (LivePreviewPanel) posts design
  // patches into this iframe so changes apply INSTANTLY without a full gallery
  // reload (which re-fetched every image and felt like lag).
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "lumetier-preview-limit") { setPreviewLimit(Number(e.data.limit) || 0); return; }
      if (e.data?.type !== "lumetier-design-patch" || !e.data.patch) return;
      setGallery((g) => (g ? { ...g, ...e.data.patch } : g));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Tell the Design Studio the gallery data has landed (so it can drop its
  // "loading preview" overlay) and how many photos the capped preview shows.
  useEffect(() => {
    if (!isDesign || loading || !gallery || window.parent === window) return;
    const total = (gallery.photos || []).length;
    try {
      window.parent.postMessage(
        { type: "lumetier-preview-ready", total, shown: previewLimit ? Math.min(total, previewLimit) : total },
        window.location.origin,
      );
    } catch { /* not embedded */ }
  }, [isDesign, loading, gallery, previewLimit]);

  // Persist in-progress favorites so a refresh / revisit keeps the pick list.
  useEffect(() => {
    try { localStorage.setItem(`lumetier_proof_favs_${slug}`, JSON.stringify(favorites)); } catch { /* quota */ }
  }, [favorites, slug]);

  // Returning client: load the last submitted selection and pre-populate the
  // hearts (unless there's already local in-progress work, which wins). Lets
  // Ada come back to her 50 picks and narrow them down to 10.
  useEffect(() => {
    if (!gallery?.unlocked || gallery.type === "interactive" || gallery.is_delivered) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = password ? { "x-gallery-password": password } : {};
        const { data } = await axios.get(`${API}/galleries/${slug}/selections/latest`, { headers });
        if (cancelled || !data?.found) return;
        setPrevSelection({
          count: (data.items || []).length,
          submitted_at: data.submitted_at,
          client_name: data.client_name || "",
          submissions: data.submissions || 1,
        });
        setClientName((n) => n || data.client_name || "");
        setClientEmail((e) => e || data.client_email || "");
        // Signature of the server's merged selection. Local (unsubmitted)
        // edits only win when they were seeded from THIS exact snapshot —
        // otherwise the local copy is stale (older device visit, or the
        // client submitted again elsewhere) and the server set is restored.
        const sig = `${data.submissions || 1}:${(data.items || []).length}:${data.submitted_at || ""}`;
        let seededFrom = null;
        try { seededFrom = localStorage.getItem(`lumetier_proof_base_${slug}`); } catch { /* private mode */ }
        setFavorites((prev) => {
          if (Object.keys(prev).length > 0 && seededFrom === sig) return prev;
          const next = {};
          (data.items || []).forEach((it) => {
            next[it.photo_id] = { favorited: true, comment: it.comment || "" };
          });
          return next;
        });
        try { localStorage.setItem(`lumetier_proof_base_${slug}`, sig); } catch { /* private mode */ }
      } catch { /* no previous selection — nothing to restore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gallery?.unlocked, slug]);

  // Primary magic-link bootstrap. When the URL contains `?primary_token=…`
  // we trade it for a long-lived collaborator token, store it, then strip the
  // query param so reloads don't re-trigger the exchange.
  useEffect(() => {
    if (!isInteractive) return;
    const params = new URLSearchParams(location.search);
    const pt = params.get("primary_token");
    if (!pt) { setClaimingPrimary(false); return; }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/galleries/${slug}/collaborators/claim-primary`, { primary_token: pt });
        await collab.setToken(data.token);
        toast.success(`Welcome back, ${data.display_name}`);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid primary link");
      } finally {
        // Clear the race guard so the gate can render if the claim failed
        // (e.g. revoked / expired token) — without this, an invalid magic
        // link would hang on a blank screen forever.
        setClaimingPrimary(false);
        params.delete("primary_token");
        navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInteractive, location.search, slug]);

  async function submitPassword(e) {
    e?.preventDefault();
    if (!password) return;
    setAuthenticating(true);
    try {
      await axios.post(`${API}/galleries/${slug}/auth`, { password });
      await loadGallery(password);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Incorrect password");
    } finally {
      setAuthenticating(false);
    }
  }

  // ── Derived ─────────────────────────────────────────────────────────
  const needsPassword = gallery && gallery.has_password && !unlocked;
  const needsCollaborator = isInteractive && unlocked && !collab.loading && !collab.token && !claimingPrimary;
  const photos = gallery?.photos || [];
  const favCount = Object.values(favorites).filter((f) => f.favorited).length;
  const includedEdits = gallery?.included_edits || 0;
  const extraPrice = gallery?.extra_edit_price ?? 35;
  const extraCount = includedEdits > 0 ? Math.max(0, favCount - includedEdits) : 0;
  const extraTotal = +(extraCount * extraPrice).toFixed(2);
  const maxSel = gallery?.max_selections || 0;

  // ── Interactive: filtered/sorted photo list ────────────────────────
  const interactivePhotos = useMemo(() => {
    if (!isInteractive || favView === "all") return photos;
    if (favView === "mine") {
      return photos.filter((p) => favsServer.isMine(p.id));
    }
    if (favView === "everyone") {
      return photos.filter((p) => (favsServer.byPhoto[p.id]?.count || 0) > 0);
    }
    if (favView === "heatmap") {
      // Show all photos but sort by count desc, ties broken by original order.
      return [...photos].sort((a, b) => {
        const ca = favsServer.byPhoto[a.id]?.count || 0;
        const cb = favsServer.byPhoto[b.id]?.count || 0;
        return cb - ca;
      });
    }
    return photos;
  }, [isInteractive, favView, photos, favsServer]);

  const interactiveCounts = useMemo(() => ({
    mine: favsServer.mine?.size || 0,
    everyone: Object.values(favsServer.byPhoto || {}).filter((e) => (e.count || 0) > 0).length,
  }), [favsServer]);

  // ── Proofing: "selected only" view filter ───────────────────────────
  const proofPhotos = useMemo(() => {
    if (isInteractive || proofView !== "selected") return photos;
    return photos.filter((p) => favorites[p.id]?.favorited);
  }, [isInteractive, proofView, photos, favorites]);
  useEffect(() => {
    if (favCount === 0 && proofView !== "all") setProofView("all");
  }, [favCount, proofView]);

  // ── Favorites (proofing — local) ────────────────────────────────────
  function toggleFav(photoId) {
    setFavorites((prev) => {
      const cur = prev[photoId] || { favorited: false, comment: "" };
      const next = { ...cur, favorited: !cur.favorited };
      if (!next.favorited && !next.comment) {
        const { [photoId]: _, ...rest } = prev;
        void _;
        return rest;
      }
      return { ...prev, [photoId]: next };
    });
  }

  function setComment(photoId, comment) {
    setFavorites((prev) => {
      const cur = prev[photoId] || { favorited: false, comment: "" };
      const next = { ...cur, comment };
      if (!next.favorited && !next.comment) {
        const { [photoId]: _, ...rest } = prev;
        void _;
        return rest;
      }
      return { ...prev, [photoId]: next };
    });
  }

  // Interactive heart toggle — proxies to server hook, with display name + role.
  // G5: when the user toggles their own heart, also broadcast a heart_burst
  // to the live room so the rest of the watch party sees the gold pulse.
  function toggleInteractiveHeart(photoId) {
    favsServer.toggle(photoId, collab.displayName, collab.role);
    if (liveSendRef.current) {
      try { liveSendRef.current({ type: "heart_burst", photo_id: photoId }); } catch (e) { void e; }
    }
  }

  async function submitSelections() {
    if (favCount === 0) return toast.error("Favorite at least one photo first");
    if (!clientName.trim() || clientName.trim().length < 2) return toast.error("Please enter your name");
    if (maxSel && favCount > maxSel) return toast.error(`You can pick at most ${maxSel} photos`);

    setSubmitting(true);
    try {
      const items = Object.entries(favorites)
        .filter(([, v]) => v.favorited)
        .map(([photo_id, v]) => ({ photo_id, comment: v.comment || "" }));
      const headers = password ? { "x-gallery-password": password } : {};
      const { data } = await axios.post(
        `${API}/galleries/${slug}/select`,
        {
          items,
          photo_ids: items.map((it) => it.photo_id),
          client_name: clientName.trim(),
          client_email: clientEmail || undefined,
          message: overallMessage || "",
        },
        { headers },
      );
      setSubmittedResult(data);
      setShowSubmit(false);
      toast.success("Selections submitted — your photographer will be in touch!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Download flow ───────────────────────────────────────────────────
  async function verifyDownloadPassword(e) {
    e?.preventDefault();
    setVerifyingDl(true);
    try {
      const headers = password ? { "x-gallery-password": password } : {};
      await axios.post(`${API}/galleries/${slug}/download-auth`, { password: downloadPassword }, { headers });
      setDownloadUnlocked(true);
      try { sessionStorage.setItem(`gallery_dlpw_${slug}`, downloadPassword); } catch (err) { console.warn(err); }
      toast.success("Downloads unlocked");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Incorrect password");
    } finally {
      setVerifyingDl(false);
    }
  }

  useEffect(() => {
    if (!gallery?.requires_download_password) {
      if (gallery?.is_delivered) setDownloadUnlocked(true);
      return;
    }
    try {
      const stored = sessionStorage.getItem(`gallery_dlpw_${slug}`);
      if (stored) setDownloadPassword(stored);
    } catch (err) { console.warn(err); }
  }, [gallery, slug]);

  async function downloadOne(photo) {
    try {
      const params = new URLSearchParams();
      if (password) params.set("pw", password);
      if (downloadPassword) params.set("dpw", downloadPassword);
      const qs = params.toString();
      const url = `${API}/galleries/${slug}/photos/${photo.id}/download${qs ? `?${qs}` : ""}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = photo.filename || `${photo.id}.jpg`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    }
  }

  async function downloadZip() {
    setZipping(true);
    try {
      const params = new URLSearchParams();
      if (password) params.set("pw", password);
      if (downloadPassword) params.set("dpw", downloadPassword);
      const qs = params.toString();
      const url = `${API}/galleries/${slug}/zip${qs ? `?${qs}` : ""}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(gallery.name || "gallery").replace(/[^a-z0-9_-]+/gi, "_")}.zip`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    } finally {
      setTimeout(() => setZipping(false), 1500);
    }
  }

  // ── Lightbox controls ──────────────────────────────────────────────
  const photosForLightbox = isInteractive ? interactivePhotos : proofPhotos;

  function openLightboxByPhoto(photo) {
    const idx = photosForLightbox.findIndex((p) => p.id === photo.id);
    if (idx >= 0) openLightbox(idx);
  }
  function openLightbox(idx) {
    setLightboxIdx(idx);
    const p = photosForLightbox[idx];
    setCommentDraft((p && favorites[p.id]?.comment) || "");
  }
  function closeLightbox() { setLightboxIdx(null); setSlideshowOn(false); }
  function lbPrev() {
    if (lightboxIdx == null) return;
    const i = (lightboxIdx - 1 + photosForLightbox.length) % photosForLightbox.length;
    setLightboxIdx(i);
    setCommentDraft(favorites[photosForLightbox[i].id]?.comment || "");
  }
  function lbNext() {
    if (lightboxIdx == null) return;
    const i = (lightboxIdx + 1) % photosForLightbox.length;
    setLightboxIdx(i);
    setCommentDraft(favorites[photosForLightbox[i].id]?.comment || "");
  }
  function lbSaveComment() {
    if (lightboxIdx == null) return;
    const p = photosForLightbox[lightboxIdx];
    setComment(p.id, commentDraft);
    toast.success(commentDraft ? "Note saved" : "Note removed");
  }
  function startSlideshow() {
    setLightboxIdx(0);
    setCommentDraft(favorites[photosForLightbox[0]?.id]?.comment || "");
    setSlideshowOn(true);
  }

  // ── Render branches ─────────────────────────────────────────────────
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  // Password gate first (for both standard and interactive galleries)
  if (needsPassword) {
    return (
      <div style={themeStyle} className="gallery-themed">
        <Toaster richColors position="top-center" />
        <PremiumPasswordGate
          gallery={gallery}
          password={password}
          setPassword={setPassword}
          onSubmit={submitPassword}
          authenticating={authenticating}
        />
      </div>
    );
  }
  if (!gallery) return null;

  // Interactive gallery — identity gate
  if (needsCollaborator) {
    return (
      <div style={themeStyle} className="gallery-themed">
        <Toaster richColors position="top-center" />
        <CollaboratorGate
          gallery={gallery}
          onVerified={(token) => collab.setToken(token)}
        />
      </div>
    );
  }

  if (submittedResult) {
    return <SubmittedView gallery={gallery} clientName={clientName} result={submittedResult} />;
  }

  // ── Main gallery ────────────────────────────────────────────────────
  const isDelivered = gallery.is_delivered;
  const requiresDlPw = gallery.requires_download_password && !downloadUnlocked;
  const downloadsAvailable = isDelivered && (downloadUnlocked || !gallery.requires_download_password);

  // Interactive header strip — view toggle + collaborator badge + sign out
  const interactiveHeader = isInteractive ? (
    <div
      className="max-w-6xl mx-auto px-4 sm:px-8 pt-4 sm:pt-5 flex flex-wrap items-center justify-between gap-3"
      data-testid="interactive-header"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-stone-700">
        <span
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: accent || "#C9A961" }}
        />
        Hi, <span className="text-stone-900 normal-case tracking-normal text-xs font-medium">{collab.displayName}</span>
        {collab.role === "primary" && (
          <span className="px-1.5 py-0.5 text-[9px] tracking-[0.22em] uppercase bg-stone-900 text-[#F5EFE0]">primary</span>
        )}
      </div>
      <div className="flex items-center gap-3 ml-auto">
        <FavoritesViewToggle value={favView} onChange={setFavView} counts={interactiveCounts} />
        <button
          type="button"
          onClick={() => setOnboardingOpen(true)}
          className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-stone-300 text-stone-600 hover:text-stone-900 hover:border-stone-500 transition-colors"
          data-testid="onboarding-reopen-btn"
          title="How this gallery works"
          aria-label="How this gallery works"
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
        {collab.role === "primary" && (
          <PrimaryNotificationsBell
            slug={slug}
            token={collab.token}
            accent={accent || "#C9A961"}
            onJumpToPhoto={(pid) => {
              const p = photosForLightbox.find((x) => x.id === pid);
              if (p) openLightboxByPhoto(p);
            }}
          />
        )}
        {collab.role === "primary" && !isLockedSubmitted && (
          <button
            onClick={startOrResumeLive}
            className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] uppercase tracking-[0.22em] border border-[#C9A961]/60 text-stone-900 hover:bg-[#C9A961] transition-colors"
            data-testid="start-live-session-btn"
            title="Start a live watch-party with your collaborators"
          >
            <Radio className="w-3 h-3" />
            {liveRoomId ? "Resume live" : "Start live session"}
          </button>
        )}
        <button
          onClick={() => { collab.clear(); toast.success("Signed out"); }}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900 transition-colors"
          data-testid="interactive-signout"
          title="Sign out as this collaborator"
        >
          <LogOut className="w-3 h-3" /> Sign out
        </button>
      </div>
    </div>
  ) : null;

  // Bundle handed to every template. Templates render the InteractiveHeart
  // when `ui.interactive.enabled` is true (and skip the proofing heart in that
  // case — interactive galleries don't currently use the selection-submission
  // flow; that's Phase G4).
  const ui = {
    favorites,
    isDelivered,
    downloadsAvailable,
    designPreview: isDesign,
    selectedCount: favCount,
    phaseBanner: (
      <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-4 space-y-3">
        <PhaseBanner isDelivered={isDelivered} includedEdits={includedEdits} extraPrice={extraPrice} />
        {!isDelivered && !isInteractive && prevSelection && (
          <WelcomeBackBanner
            prev={prevSelection}
            favCount={favCount}
            view={proofView}
            onToggleView={() => setProofView((v) => (v === "selected" ? "all" : "selected"))}
          />
        )}
      </div>
    ),
    downloadBar: isDelivered ? (
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <DownloadBar
          requiresDlPw={requiresDlPw}
          downloadPassword={downloadPassword}
          setDownloadPassword={setDownloadPassword}
          onVerify={verifyDownloadPassword}
          verifyingDl={verifyingDl}
          zipping={zipping}
          onZip={downloadZip}
        />
      </div>
    ) : null,
    selectionBar: !isDelivered && !isInteractive && favCount > 0 ? (
      <SelectionBar
        favCount={favCount}
        includedEdits={includedEdits}
        extraCount={extraCount}
        extraTotal={extraTotal}
        maxSel={maxSel}
        onOpenSubmit={() => setShowSubmit(true)}
        view={proofView}
        onViewChange={setProofView}
        isUpdate={!!prevSelection}
      />
    ) : null,
    interactive: {
      enabled: isInteractive,
      isMine: favsServer.isMine,
      getEntry: favsServer.getEntry,
      toggle: toggleInteractiveHeart,
      renderHeart: (photo, size = "md") => (
        <InteractiveHeart
          isMine={favsServer.isMine(photo.id)}
          count={favsServer.getEntry(photo.id).count}
          collaborators={favsServer.getEntry(photo.id).collaborators}
          onToggle={() => toggleInteractiveHeart(photo.id)}
          size={size}
          testid={`heart-photo-${photo.id}`}
        />
      ),
      headerStrip: interactiveHeader,
      commentCount: (photoId) => commentsHook.summary[photoId] || 0,
      openComments: openCommentsFor,
      renderCommentBadge: (photo) => (
        <CommentBadge
          count={commentsHook.summary[photo.id] || 0}
          onClick={() => openCommentsFor(photo)}
          testid={`comment-badge-${photo.id}`}
        />
      ),
      // G5.b — visceral live signals per photo card
      live: {
        active: !!(liveOpen && liveRoomId),
        participants: liveParticipants,
        meId: liveMeId,
        bursts: liveBursts,
      },
      renderLiveOverlay: (photo) => (
        (liveOpen && liveRoomId) ? (
          <PhotoLiveOverlay
            photoId={photo.id}
            participants={liveParticipants}
            meId={liveMeId}
            burstKey={liveBursts[photo.id]}
            burstLabel={undefined}
          />
        ) : null
      ),
    },
  };

  const actions = {
    openLightbox: openLightboxByPhoto,
    toggleFav,
    downloadOne,
    startSlideshow,
  };

  // Hand the template a (possibly filtered/sorted) photo list. Design Studio
  // preview is capped so it loads in seconds, not minutes.
  const templatePhotos = isInteractive ? interactivePhotos : proofPhotos;
  const galleryForTemplate = {
    ...gallery,
    photos: isDesign && previewLimit ? templatePhotos.slice(0, previewLimit) : templatePhotos,
    design_preview: isDesign,
  };

  const Template = TEMPLATES[gallery.template] || EditorialTemplate;

  return (
    <div style={themeStyle} className="gallery-themed" data-testid="public-gallery-root">
      <Toaster richColors position="top-center" />
      <Template gallery={galleryForTemplate} accent={accent} ui={ui} actions={actions} />
      <StorageStatusBanner />

      {showSubmit && (
        <SubmitModal
          favCount={favCount}
          includedEdits={includedEdits}
          extraCount={extraCount}
          extraPrice={extraPrice}
          extraTotal={extraTotal}
          clientName={clientName}
          setClientName={setClientName}
          clientEmail={clientEmail}
          setClientEmail={setClientEmail}
          overallMessage={overallMessage}
          setOverallMessage={setOverallMessage}
          submitting={submitting}
          onClose={() => setShowSubmit(false)}
          onSubmit={submitSelections}
        />
      )}

      <AnimatePresence>
        {lightboxIdx != null && photosForLightbox[lightboxIdx] && (
          <PremiumLightbox
            photo={photosForLightbox[lightboxIdx]}
            isFav={isInteractive
              ? favsServer.isMine(photosForLightbox[lightboxIdx].id)
              : !!favorites[photosForLightbox[lightboxIdx].id]?.favorited}
            comment={commentDraft}
            setComment={setCommentDraft}
            onSaveComment={lbSaveComment}
            onToggleFav={() => {
              if (isInteractive) toggleInteractiveHeart(photosForLightbox[lightboxIdx].id);
              else toggleFav(photosForLightbox[lightboxIdx].id);
            }}
            onClose={closeLightbox}
            onPrev={lbPrev}
            onNext={lbNext}
            mode={isDelivered ? "delivery" : "proofing"}
            downloadsAvailable={downloadsAvailable}
            onDownload={() => downloadOne(photosForLightbox[lightboxIdx])}
            idx={lightboxIdx}
            total={photosForLightbox.length}
            prevPhotoUrl={photosForLightbox[lightboxIdx - 1]?.url}
            nextPhotoUrl={photosForLightbox[lightboxIdx + 1]?.url}
            isSlideshow={slideshowOn}
            toggleSlideshow={() => setSlideshowOn((s) => !s)}
            slideshowDuration={4500}
            watermark={gallery.watermark_enabled && !downloadsAvailable}
            interactive={isInteractive ? {
              enabled: true,
              count: favsServer.getEntry(photosForLightbox[lightboxIdx].id).count,
              collaborators: favsServer.getEntry(photosForLightbox[lightboxIdx].id).collaborators,
              commentCount: commentsHook.summary[photosForLightbox[lightboxIdx].id] || 0,
              onOpenComments: () => openCommentsFor(photosForLightbox[lightboxIdx]),
            } : null}
          />
        )}
      </AnimatePresence>

      {/* G3 comment drawer — only mounted for interactive galleries; gated by `open` */}
      {isInteractive && (
        <CommentDrawer
          open={commentDrawerOpen}
          photo={commentDrawerPhoto}
          me={collab.me}
          hook={commentsHook}
          onClose={() => setCommentDrawerOpen(false)}
        />
      )}

      {/* First-visit onboarding splash — also reopenable via the "?" pill in the interactive header */}
      {isInteractive && collab.token && collab.me?.id && (
        <InteractiveOnboardingModal
          open={onboardingOpen}
          onClose={closeOnboarding}
          role={collab.role}
          accent={accent}
        />
      )}

      {/* G5 — live session panel */}
      {isInteractive && liveOpen && liveRoomId && collab.token && (
        <LiveSessionPanel
          slug={slug}
          roomId={liveRoomId}
          token={collab.token}
          shareUrl={liveShareUrl || `${window.location.origin}/gallery/${slug}/live/${liveRoomId}`}
          isHost={collab.role === "primary"}
          currentPhotoId={lightboxIdx != null ? photosForLightbox[lightboxIdx]?.id : null}
          onParticipantsChange={(participants, myId) => {
            setLiveParticipants(participants);
            if (myId) setLiveMeId(myId);
          }}
          onSendBound={(sender) => { liveSendRef.current = sender; }}
          onHeartBurst={(photoId, who) => {
            setLiveBursts((b) => ({ ...b, [photoId]: (b[photoId] || 0) + 1 }));
            // Clear after the animation completes so re-trigger plays again.
            setTimeout(() => {
              setLiveBursts((b) => {
                const next = { ...b };
                delete next[photoId];
                return next;
              });
            }, 600);
            if (who) toast.message(`${who} loved a photo`, { duration: 1400 });
          }}
          onClose={() => {
            liveSendRef.current = null;
            setLiveOpen(false);
            setLiveParticipants([]);
          }}
        />
      )}

      {/* G4 — primary client's "send selection" footer CTA + modal */}
      {isInteractive && (
        <>
          <SelectionFooterCta
            role={collab.role}
            isSubmitted={isLockedSubmitted}
            selectedCount={isLockedSubmitted
              ? (gallery?.photos || []).filter((p) => favsServer.byPhoto[p.id]?.count > 0).length || favsServer.mine?.size || 0
              : Object.values(favsServer.byPhoto || {}).filter((e) => (e.count || 0) > 0).length}
            includedEdits={gallery.included_edits || 0}
            extraEditPrice={gallery.extra_edit_price}
            onOpenSubmit={() => setSubmitOpen(true)}
          />
          <SelectionSubmitModal
            slug={slug}
            token={collab.token}
            open={submitOpen}
            onClose={() => setSubmitOpen(false)}
            onSubmitted={(result) => {
              setSubmitOpen(false);
              setSubmitted(true);
              toast.success(`Selection sent — ${result.photo_count} photos.`);
              // Re-fetch gallery state for hard lock & re-render reads
              loadGallery(password || undefined);
            }}
          />
        </>
      )}
    </div>
  );
}
